import {
  Alert,
  AnimatedContentLoader,
  Button,
  DropdownMenu,
  Input,
  PillFilter,
  PillToggle,
  SingleColumnLayout,
  Tab,
  Tabs
} from '@components'
import { getExpenseClaimExpensePageUrl, getExpenseClaimTripPageUrl } from '@constants'
import { PermissionSet, useBusinessContext, usePermission } from '@context'
import {
  ExpenseClaimStatus,
  collectFromPages,
  createExpenseClaim,
  expenseClaimFieldError,
  fetchExpenseClaimProfile,
  fetchExpenseClaims,
  saveExpenseClaimProfile,
  useCurrentUser
} from '@query'
import { useScreen } from '@utils'
import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient
} from '@tanstack/react-query'
import toast from 'react-hot-toast'
import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FaCar, FaCaretDown, FaPlus, FaReceipt } from 'react-icons/fa'
import { useHistory, useLocation } from 'react-router-dom'
import styled from 'styled-components'
import { IncomesRegisterTab } from './IncomesRegisterTab'
import { CLAIM_STATUSES } from './shared'
import { ClaimHeader, ClaimList, ClaimListLoadingRow, ClaimTableShell, EmptyRow } from './ClaimList'

type ClaimFilter = 'all' | 'awaiting_you' | ExpenseClaimStatus

// Same width where the documents header drops the "New document" label and keeps the icon.
const ICON_ONLY_PRIMARY_MAX_WIDTH = 1350

const profileKey = (slug: string) => [slug, 'expense-claim-profile']

export const TravelAndExpensesPage: React.FC = () => {
  const [t] = useTranslation()
  const canEdit = usePermission(PermissionSet.EXPENSE_CLAIMS_EDIT)
  const canApprove = usePermission(PermissionSet.EXPENSE_CLAIMS_APPROVE)

  return (
    <SingleColumnLayout header={t('travelAndExpenses.title')}>
      <TabsWrapper>
        <Tabs toolbar fillHeight queryKey="tab">
          <Tab
            key="claims"
            name={t('travelAndExpenses.tabs.claims')}
            actions={canEdit && <NewClaimMenu />}
          >
            <ClaimsTab canApprove={canApprove} />
          </Tab>
          {canApprove && (
            <Tab key="incomes" name={t('travelAndExpenses.tabs.incomes')}>
              <IncomesRegisterTab />
            </Tab>
          )}
          <Tab key="details" name={t('travelAndExpenses.tabs.details')}>
            <MyDetailsTab canEdit={canEdit} />
          </Tab>
        </Tabs>
      </TabsWrapper>
    </SingleColumnLayout>
  )
}

const NewClaimMenu: React.FC = () => {
  const [t] = useTranslation()
  const history = useHistory()
  const queryClient = useQueryClient()
  const { slug } = useBusinessContext()

  const { width } = useScreen()
  const label = t('travelAndExpenses.newClaim')
  const iconOnly = width <= ICON_ONLY_PRIMARY_MAX_WIDTH

  const createForTrip = useMutation({
    mutationFn: () => createExpenseClaim(slug),
    onSuccess: claim => {
      queryClient.invalidateQueries({ queryKey: [slug, 'expense-claims'] })
      history.push(getExpenseClaimTripPageUrl(slug, claim.id, 'new'))
    },
    onError: () => toast.error(t('travelAndExpenses.loadError'))
  })

  const createForExpense = useMutation({
    mutationFn: () => createExpenseClaim(slug),
    onSuccess: claim => {
      queryClient.invalidateQueries({ queryKey: [slug, 'expense-claims'] })
      history.push(getExpenseClaimExpensePageUrl(slug, claim.id, 'new'))
    },
    onError: () => toast.error(t('travelAndExpenses.loadError'))
  })

  const creating = createForTrip.isPending || createForExpense.isPending

  return (
    <DropdownMenu
      items={[
        <Button
          key="add-trip"
          icon={<FaCar />}
          disabled={creating}
          onClick={() => createForTrip.mutate()}
        >
          {t('travelAndExpenses.list.addTrip')}
        </Button>,
        <Button
          key="add-expense"
          icon={<FaReceipt />}
          disabled={creating}
          onClick={() => createForExpense.mutate()}
        >
          {t('travelAndExpenses.list.addExpense')}
        </Button>
      ]}
    >
      <Button
        icon={iconOnly ? <FaPlus /> : undefined}
        iconRight={iconOnly ? undefined : <FaCaretDown />}
        aria-label={iconOnly ? label : undefined}
        showSpinner={creating}
      >
        {iconOnly ? null : label}
      </Button>
    </DropdownMenu>
  )
}

const ClaimsTab: React.FC<{ canApprove: boolean }> = ({ canApprove }) => {
  const [t] = useTranslation()
  const { slug } = useBusinessContext()
  const { data: currentUser } = useCurrentUser()
  const [filter, setFilter] = useState<ClaimFilter>('all')
  const [mineOnly, setMineOnly] = useState(false)

  const listScope = canApprove ? 'combined' : 'mine'

  const { data, isLoading, isError, hasNextPage, fetchNextPage, isFetchingNextPage } =
    useInfiniteQuery({
      queryKey: [slug, 'expense-claims', listScope, filter, mineOnly],
      initialPageParam: 1,
      queryFn: ({ pageParam = 1 }) =>
        fetchExpenseClaims(
          { slug },
          {
            scope: listScope,
            page: pageParam,
            list_filter: filter,
            mine_only: mineOnly ? 'true' : undefined
          }
        ),
      getNextPageParam: lastPage => lastPage.next,
      getPreviousPageParam: lastPage => lastPage.prev,
      placeholderData: keepPreviousData
    })

  const visible = useMemo(() => collectFromPages(data), [data])
  const filterCounts = data?.pages[0]?.filter_counts ?? {}

  const filterValues: ClaimFilter[] = useMemo(() => {
    if (!canApprove) {
      return ['all', ...CLAIM_STATUSES]
    }
    return ['all', 'awaiting_you', 'AWAITING_APPROVAL', 'DRAFT', 'APPROVED', 'PAID']
  }, [canApprove])

  const loadMore = () => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage()
    }
  }

  return (
    <ClaimsLayout>
      <FilterRow>
        <PillFilter<ClaimFilter>
          ariaLabel={t('travelAndExpenses.list.filters')}
          value={filter}
          onChange={setFilter}
          options={filterValues.map(value => ({
            value,
            label: t(`travelAndExpenses.list.filter.${value}`),
            count: filterCounts[value] ?? 0
          }))}
        />
        {canApprove && (
          <PillToggle
            label={t('travelAndExpenses.list.mineOnly')}
            isActive={mineOnly}
            onChange={setMineOnly}
          />
        )}
      </FilterRow>

      <AnimatedContentLoader isLoading={isLoading && !data}>
        {isError && <Alert type="error" title={t('travelAndExpenses.loadError')} />}
        <ClaimTableShell>
          <ClaimHeader>
            <span>{t('travelAndExpenses.list.columns.claim')}</span>
            <span>{t('travelAndExpenses.list.columns.submittedBy')}</span>
            <span>{t('travelAndExpenses.list.columns.date')}</span>
            <span>{t('travelAndExpenses.list.columns.stage')}</span>
            <span className="end">{t('travelAndExpenses.list.columns.amount')}</span>
            <span className="end">{t('travelAndExpenses.list.columns.status')}</span>
          </ClaimHeader>
          {visible.length > 0 ? (
            <ClaimList
              slug={slug}
              claims={visible}
              currentUserId={currentUser?.id}
              markOwnAsAwaitingYou={filter === 'awaiting_you'}
              isFetchingNextPage={isFetchingNextPage}
              onScrollBottom={loadMore}
              t={t}
            />
          ) : isFetchingNextPage ? (
            <ClaimListLoadingRow t={t} />
          ) : (
            !isLoading && <EmptyRow>{t('travelAndExpenses.list.empty')}</EmptyRow>
          )}
        </ClaimTableShell>
      </AnimatedContentLoader>
    </ClaimsLayout>
  )
}

const MyDetailsTab: React.FC<{ canEdit: boolean }> = ({ canEdit }) => {
  const [t] = useTranslation()
  const queryClient = useQueryClient()
  const location = useLocation<{ profileMissing?: string }>()
  const { slug, data: business } = useBusinessContext()
  const { data, isLoading } = useQuery({
    queryKey: profileKey(slug),
    queryFn: () => fetchExpenseClaimProfile(slug)
  })
  const [name, setName] = useState('')
  const [iban, setIban] = useState('')
  const [homeAddress, setHomeAddress] = useState('')
  const [nameError, setNameError] = useState<string | null>(null)
  const [ibanError, setIbanError] = useState<string | null>(null)
  const [homeAddressError, setHomeAddressError] = useState<string | null>(null)

  useEffect(() => {
    if (!data) return
    setName(data.name)
    setIban(data.iban)
    setHomeAddress(data.home_address)
  }, [data])

  const save = useMutation({
    mutationFn: () => saveExpenseClaimProfile(slug, { name, iban, home_address: homeAddress }),
    onSuccess: profile => {
      queryClient.setQueryData(profileKey(slug), profile)
      setIban(profile.iban)
      setIbanError(null)
      setNameError(null)
      setHomeAddressError(null)
      toast.success(t('travelAndExpenses.details.saveSuccess'))
    },
    onError: error => {
      const ibanMessage = expenseClaimFieldError(error, 'iban')
      const nameMessage = expenseClaimFieldError(error, 'name')
      const homeAddressMessage = expenseClaimFieldError(error, 'home_address')
      setIbanError(ibanMessage)
      setNameError(nameMessage)
      setHomeAddressError(homeAddressMessage)
      if (!ibanMessage && !nameMessage && !homeAddressMessage) {
        toast.error(expenseClaimFieldError(error, 'detail') ?? t('travelAndExpenses.loadError'))
      }
    }
  })

  const isFirstVisit = !!data && !data.name && !data.iban

  return (
    <AnimatedContentLoader isLoading={isLoading}>
      <DetailsLayout>
        {location.state?.profileMissing && (
          <Alert type="error" marginBottom={0} title={location.state.profileMissing} />
        )}
        {isFirstVisit && (
          <Alert
            type="info"
            marginBottom={0}
            description={t('travelAndExpenses.details.intro', { business: business?.name })}
          />
        )}
        <DetailsForm
          onSubmit={event => {
            event.preventDefault()
            if (!canEdit) return
            const required = t('validation.required')
            let valid = true
            if (!name.trim()) {
              setNameError(required)
              valid = false
            }
            if (!iban.trim()) {
              setIbanError(required)
              valid = false
            }
            if (!valid) return
            setNameError(null)
            save.mutate()
          }}
        >
          <div>
            <FormTitle>{t('travelAndExpenses.details.title')}</FormTitle>
            <Help>{t('travelAndExpenses.details.privacy', { business: business?.name })}</Help>
          </div>
          <Input
            name="name"
            label={t('travelAndExpenses.details.name')}
            required
            value={name}
            disabled={!canEdit}
            errors={nameError ? { message: nameError } : undefined}
            onChange={event => {
              setName(event.target.value)
              setNameError(null)
            }}
          />
          <Input
            name="iban"
            label={t('travelAndExpenses.details.iban')}
            required
            value={iban}
            disabled={!canEdit}
            errors={ibanError ? { message: ibanError } : undefined}
            onChange={event => {
              setIban(event.target.value)
              setIbanError(null)
            }}
          />
          <Input
            name="home_address"
            label={t('travelAndExpenses.details.address')}
            info={t('travelAndExpenses.details.addressHelp')}
            value={homeAddress}
            disabled={!canEdit}
            errors={homeAddressError ? { message: homeAddressError } : undefined}
            onChange={event => {
              setHomeAddress(event.target.value)
              setHomeAddressError(null)
            }}
          />
          {canEdit && (
            <FormFooter>
              <Button type="submit" intent="primary" showSpinner={save.isPending}>
                {t('travelAndExpenses.details.save')}
              </Button>
            </FormFooter>
          )}
        </DetailsForm>
      </DetailsLayout>
    </AnimatedContentLoader>
  )
}

const TabsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  overflow: hidden;
`

const ClaimsLayout = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.md}rem;
  flex: 1;
  min-width: 0;
  min-height: 0;
`

const FilterRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm}rem;
`

const Help = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  color: ${({ theme }) => theme.colors.iconGray};
`

const DetailsLayout = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.md}rem;
`

const DetailsForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  max-width: ${({ theme }) => theme.layout.formMax}rem;
  padding: ${({ theme }) => theme.spacing.lg}rem;
  background: ${({ theme }) => theme.colors.neutralWhite};
  border-radius: ${({ theme }) => theme.borderRadius.md}rem;
`

const FormTitle = styled.h2`
  margin: 0 0 ${({ theme }) => theme.spacing.xxs}rem;
  font-size: ${({ theme }) => theme.fontSize.lg}rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.neutralBlack};
`

const FormFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  padding-top: ${({ theme }) => theme.spacing.sm}rem;
  border-top: 1px solid ${({ theme }) => theme.colors.neutralGray};
`
